import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Spin } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useBannerMobileContext } from "../../context/BannerMobileContext/BannerMobileContext";
import { BannerMobileListItem, BannerMobileOrderItem } from "../../context/BannerMobileContext/constants";
import {
	Container,
	HeaderComponent,
	ActionButtonOverlay,
} from "../../components/styled/common";
import {
	OrderItem,
	OrderItemLabel,
	SwapButton,
} from "../../components/styled/order";
import MainButtonComponent from "../../components/MainButtonComponent";


const OrderBannerMobile = () => {
	const { getBannerMobileList, bannerMobileList, operationStatus, updateBannerMobileOrders } = useBannerMobileContext();
	const [upperOrderedList, setUpperOrderedList] = useState<BannerMobileListItem[]>([]);
	const [lowerOrderedList, setLowerOrderedList] = useState<BannerMobileListItem[]>([]);
	const [isAnimating, setIsAnimating] = useState(false);
	const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const previousPositionsRef = useRef<Record<string, number>>({});
	const animationTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		getBannerMobileList();
	}, []);

	useEffect(() => {
		if (bannerMobileList && bannerMobileList.length > 0) {
			const sorted = [...bannerMobileList].sort(
				(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
			);
			setUpperOrderedList(sorted.filter((banner) => banner.isUpper));
			setLowerOrderedList(sorted.filter((banner) => !banner.isUpper));
		}
	}, [bannerMobileList]);

	useLayoutEffect(() => {
		const orderedList = [...upperOrderedList, ...lowerOrderedList];
		const nextPositions: Record<string, number> = {};
		let hasMovement = false;

		orderedList.forEach((banner) => {
			const key = String(banner.bannerId);
			const element = itemRefs.current[key];
			if (!element) return;

			const currentTop = element.getBoundingClientRect().top;
			nextPositions[key] = currentTop;

			const previousTop = previousPositionsRef.current[key];
			if (previousTop === undefined) return;

			const deltaY = previousTop - currentTop;
			if (deltaY === 0) return;

			hasMovement = true;
			element.style.transition = "none";
			element.style.transform = `translateY(${deltaY}px)`;

			requestAnimationFrame(() => {
				element.style.transition = "transform 220ms ease";
				element.style.transform = "translateY(0)";
			});
		});

		if (animationTimeoutRef.current) {
			window.clearTimeout(animationTimeoutRef.current);
		}

		if (hasMovement) {
			setIsAnimating(true);
			animationTimeoutRef.current = window.setTimeout(() => {
				setIsAnimating(false);
			}, 230);
		} else {
			setIsAnimating(false);
		}

		previousPositionsRef.current = nextPositions;

		return () => {
			if (animationTimeoutRef.current) {
				window.clearTimeout(animationTimeoutRef.current);
			}
		};
	}, [upperOrderedList, lowerOrderedList]);

	const handleSwap = (index: number, isUpper: boolean) => {
		if (isAnimating) return;
		const sourceList = isUpper ? upperOrderedList : lowerOrderedList;
		if (index >= sourceList.length - 1) return;
		const newList = [...sourceList];
		const temp = newList[index];
		newList[index] = newList[index + 1];
		newList[index + 1] = temp;

		if (isUpper) {
			setUpperOrderedList(newList);
		}
		else {
			setLowerOrderedList(newList);
		}
	};

	const handleSave = async () => {
		const orderedRequest: BannerMobileOrderItem[] = [
			...upperOrderedList.map((banner, index) => ({
				bannerId: banner.bannerId,
				displayOrder: index + 1,
			})),
			...lowerOrderedList.map((banner, index) => ({
				bannerId: banner.bannerId,
				displayOrder: index + 1,
			})),
		];

		updateBannerMobileOrders(orderedRequest);
	};

	return (
		<Container>
			<Spin spinning={operationStatus.type === "loading" && operationStatus.operation === "fetch"}>
				<HeaderComponent>Mobil Banner Sıralama</HeaderComponent>
				{upperOrderedList.length > 0 && <h4 style={{ marginBottom: 10 }}>Sayfa Başı Bannerlar</h4>}
				{upperOrderedList.map((banner, index) => (
					<OrderItem
						key={banner.bannerId}
						ref={(element) => {
							itemRefs.current[String(banner.bannerId)] = element;
						}}
					>
						<OrderItemLabel>
							{index + 1} - {banner.firstHeader}
						</OrderItemLabel>
						<SwapButton
							$isAnimating={isAnimating}
							onClick={(event) => {
								event.currentTarget.blur();
								handleSwap(index, true);
							}}
							disabled={isAnimating || index >= upperOrderedList.length - 1}
							title={isAnimating || index >= upperOrderedList.length - 1 ? undefined : "Aşağıdaki banner ile değiştir"}
						>
							<SwapOutlined rotate={90} />
						</SwapButton>
					</OrderItem>
				))}
				{lowerOrderedList.length > 0 && <h4 style={{ margin: "20px 0 10px" }}>Sayfa Sonu Bannerlar</h4>}
				{lowerOrderedList.map((banner, index) => (
					<OrderItem
						key={banner.bannerId}
						ref={(element) => {
							itemRefs.current[String(banner.bannerId)] = element;
						}}
					>
						<OrderItemLabel>
							{index + 1} - {banner.firstHeader}
						</OrderItemLabel>
						<SwapButton
							$isAnimating={isAnimating}
							onClick={(event) => {
								event.currentTarget.blur();
								handleSwap(index, false);
							}}
							disabled={isAnimating || index >= lowerOrderedList.length - 1}
							title={isAnimating || index >= lowerOrderedList.length - 1 ? undefined : "Aşağıdaki banner ile değiştir"}
						>
							<SwapOutlined rotate={90} />
						</SwapButton>
					</OrderItem>
				))}
				<ActionButtonOverlay>
					<MainButtonComponent type="default" onClick={handleSave} loading={operationStatus.type === "loading" && operationStatus.operation === "update"}>
						Kaydet
					</MainButtonComponent>
				</ActionButtonOverlay>
			</Spin>
		</Container>
	);
};

export default OrderBannerMobile;
